/**
 * How the vocabularies of steps 4, 5 and 6 are written for a person holding a phone.
 *
 * `@fk/shared` carries the labels both halves of the product read; these are read by the interface
 * only, so they live here rather than there. Every list is `Record<Union, string>`, so a value
 * added to an enum without a sentence here is a type error and never a bare database string on a
 * screen. Nothing here claims a credential the business does not hold, and the words the business
 * does not use appear nowhere, which the house-style test asserts over this whole repository.
 */
import type {
  AccessSecretKind,
  BreachState,
  CampaignStatus,
  Channel,
  ComposerKind,
  ChargeCancelReason,
  ChargeGateReason,
  ChargeHoldReason,
  ChoiceOwner,
  ClientLanguage,
  DiscardReason,
  DoNotContactDoor,
  EmailGoLiveState,
  EnquiryState,
  ExpenseCategory,
  FeedbackSource,
  InboundKeyword,
  InvoiceChargeState,
  MembershipRole,
  MessageChannel,
  MessageIntentState,
  NotificationKind,
  PromiseCode,
  Remedy,
  Sensitivity,
  SmsGoLiveState,
  StopHoldOutcome,
  SurveyHoldReason,
  SurveyState,
  TaskKind,
  TouchOutcome,
} from '@fk/shared';
import type { ReminderWithdrawnFor } from './api.ts';

/** The six promises from `docs/architecture.md` section 5, as short sentences. */
export const PROMISE_CODE_LABELS: Record<PromiseCode, string> = Object.freeze({
  told_before_the_visit: 'Told before the visit',
  no_silent_change: 'A change is never silent',
  same_day_reply: 'A reply the same day',
  reclean_within_24h: 'A re-clean within a day',
  invoice_within_2_days: 'The invoice within two days',
  price_matches_the_card: 'The price matches the card',
});

export const BREACH_STATE_LABELS: Record<BreachState, string> = Object.freeze({
  open: 'Open',
  remedied: 'Put right',
  waived: 'Waived',
  sanctioned: 'Sanctioned by a published rule',
});

/** The published policy: a free re-clean or a full refund, and the choice is the client's. */
export const REMEDY_LABELS: Record<Remedy, string> = Object.freeze({
  reclean: 'A re-clean at no charge',
  refund: 'A refund',
  repair: 'A repair',
  replacement: 'A replacement',
  none_agreed: 'Nothing agreed',
});

export const CHOICE_OWNER_LABELS: Record<ChoiceOwner, string> = Object.freeze({
  client: 'The client chose',
  us: 'We chose',
});

export const ACCESS_SECRET_KIND_LABELS: Record<AccessSecretKind, string> = Object.freeze({
  door_code: 'Door code',
  lockbox_code: 'Lockbox code',
  gate_code: 'Gate code',
  garage_code: 'Garage code',
  alarm_code: 'Alarm code',
  key_location: 'Where the key is kept',
  wifi: 'Wi-Fi',
  other: 'Other',
});

export const SENSITIVITY_LABELS: Record<Sensitivity, string> = Object.freeze({
  normal: 'Crew may see it in the window',
  owner_only: 'Owner only',
});

/** Published: card, bank transfer, check or cash, no surcharge on any of them. */

export const NOTIFICATION_KIND_LABELS: Record<NotificationKind, string> = Object.freeze({
  access_problem: 'Could not get in',
  issue_reported: 'Something went wrong at a house',
  promise_breach: 'A promise was missed',
  task_due: 'Something is due',
  payment_late: 'A payment is late',
  // A message not handed over, a status the website would not take, and a forget ops could not do.
  handoff_failed: 'Something did not go through',
  card_mismatch: 'The card and the page disagree',
  // A kill rule reached its number, its day or a month that broke it (ops #48): a decision to make.
  kill_rule_fired: 'A rule has fired',
  // A text whose words may ask us to stop texting, held for a person to decide (ops #30).
  opt_out_hold: 'A stop in their words waits for a decision',
});

/**
 * WHAT THE MONEY GOING OUT WAS FOR. The twelve categories `expenses` carries, in the words the
 * person typing the row would use.
 *
 * Two of them are written the way this business writes them rather than the way a form usually
 * does. Cover is what `first-20-clients.md` calls the policy it has not bought yet, and the words
 * this business may not use about itself are the ones in `what-we-cannot-say.md`: an expense row
 * records money that went out and never a credential anybody holds.
 *
 * Only Marketing lands in a channel's cost on the Sources report, which is a fact the spend form
 * says out loud where a person chooses one, because the report filters on it and a letter batch
 * filed under anything else counts towards nothing at all.
 */
export const EXPENSE_CATEGORY_LABELS: Record<ExpenseCategory, string> = Object.freeze({
  insurance: 'Cover',
  bond: 'Bond',
  supplies: 'Supplies',
  equipment: 'Equipment',
  marketing: 'Marketing',
  software: 'Software',
  phone: 'Phone',
  vehicle: 'Vehicle',
  state_fees: 'State fees',
  professional: 'Professional fees',
  cleaner_pay: 'What a clean paid',
  other: 'Something else',
});

/**
 * How an enquiry or a client arrived. One spelling, read wherever a channel is shown: the client
 * book, the client, the new-client form and the enquiries. Every paid channel is named apart from
 * the free one it would otherwise share a word with, because each carries its own kill rule and a
 * kill rule counted against the wrong channel is one that never fires. That is why Facebook and
 * Facebook ads read differently here, and why the two words are worth the room they take.
 */
export const CHANNEL_LABELS: Record<Channel, string> = Object.freeze({
  website_form: 'Website form',
  website_call: 'Website, call button',
  phone: 'Phone call',
  sms: 'Text message',
  email: 'Email',
  referral: 'Referral',
  personal_network: 'Personal network',
  realtor: 'Realtor or property manager',
  nextdoor: 'Nextdoor',
  nextdoor_ads: 'Nextdoor ads',
  facebook: 'Facebook',
  facebook_ads: 'Facebook and Instagram ads',
  google: 'Google',
  thumbtack: 'Thumbtack',
  local_services: 'Google Local Services',
  search_ads: 'Search ads',
  chatgpt_ads: 'ChatGPT ads',
  oven_hook: 'The oven hook',
  letter: 'Letter',
  in_person: 'In person',
  other: 'Other',
});

/**
 * THE LANGUAGE A HOUSEHOLD READS (ops #98, `docs/plan-client-language.md` section 3), named in
 * English for the office, as "Reads: Spanish" on the client card.
 */
export const CLIENT_LANGUAGE_LABELS: Record<ClientLanguage, string> = Object.freeze({
  en: 'English',
  es: 'Spanish',
});

/**
 * Where a campaign stands, in the office's words. It is what somebody says about the campaign and
 * never what decides the counting: a campaign's figures go by its days, and a paused one (ops #49)
 * counts its spend and its people exactly as a running one does.
 */
export const CAMPAIGN_STATUS_LABELS: Record<CampaignStatus, string> = Object.freeze({
  planned: 'Planned',
  running: 'Running',
  paused: 'Paused',
  ended: 'Ended',
});

/**
 * How a message goes out, which is a shorter list than the one above: an enquiry can arrive by
 * Nextdoor, but nothing is ever sent there. The two spellings a channel already had are the ones
 * here, so the change panel and the history below it say the same word for the same channel.
 */
export const MESSAGE_CHANNEL_LABELS: Record<MessageChannel, string> = Object.freeze({
  sms: 'Text message',
  email: 'Email',
  call: 'Phone call',
});

/**
 * WHERE OPS'S GO-LIVE SWITCH FOR TEXTS STANDS (ops #31), as the one line the move flow and the invoice
 * screen show on a text: nothing at all once it is on, since then the switch is not what decides.
 */
export const SMS_GO_LIVE_LINES: Record<SmsGoLiveState, string | null> = Object.freeze({
  off: 'Texting is off in ops',
  test_handsets: 'Texting is on for test handsets only',
  on: null,
});

/**
 * WHERE OPS'S GO-LIVE SWITCH FOR CLIENT EMAIL STANDS (ops #76 O6), texting's twin: the one line the
 * move flow and the invoice screen show on an email, and nothing once it is on.
 */
export const EMAIL_GO_LIVE_LINES: Record<EmailGoLiveState, string | null> = Object.freeze({
  off: 'Client email is off in ops',
  test_addresses: 'Client email is on for test addresses only',
  on: null,
});

/**
 * HOW A CONSENT WAS TAKEN. Kept in `@fk/shared` since ops #51, because the Worker's refusal of a text
 * names how a consent that does not count for texts was given, in the same words the household page
 * uses; re-exported here so every screen still finds it beside the other labels.
 *
 * AND THE TWENTY BESIDE IT, in `@fk/shared` since the household timeline (ops #84): its read writes
 * each row's words on the server, in the words these pills already said, so there is one copy of each.
 */
export {
  CAPTURE_METHOD_LABELS,
  ISSUE_KIND_LABELS,
  ISSUE_STATE_LABELS,
  INVOICE_STATE_LABELS,
  PAYMENT_KIND_LABELS,
  PAYMENT_METHOD_LABELS,
  ENTRY_SOURCE_LABELS,
  TASK_STATE_LABELS,
  CALL_OUTCOME_LABELS,
  INBOUND_MESSAGE_STATE_LABELS,
  MESSAGE_INTENT_STATE_LABELS,
  EMAIL_KIND_LABELS,
  FEEDBACK_FACE_LABELS,
  MEMBERSHIP_STATE_LABELS,
  MEMBERSHIP_REVOKE_REASON_LABELS,
  REVOKE_REASON_LABELS,
  HOME_REQUEST_KIND_LABELS,
  HOME_REQUEST_STATE_LABELS,
  HOME_REQUEST_CANCEL_REASON_LABELS,
  DO_NOT_CONTACT_REASON_LABELS,
  MARK_SCOPE_LABELS,
} from '@fk/shared';

/**
 * The two things this product ever asks somebody to agree to, said as a channel rather than as a
 * purpose. `arrival_notice` and `visit_photos` are in the register's vocabulary and nothing asks
 * for either yet, so neither is offered on a form: an option nobody can act on is a question that
 * gets answered wrongly.
 */
export const MESSAGE_PURPOSE_LABELS = Object.freeze({
  operational_sms: 'Texts about their cleans',
  operational_email: 'Emails about their cleans',
});

/**
 * WHAT A FOLLOW-UP IS, in the words the person typing it would use. Five kinds, and the screen
 * offers all five, so any of them can be somebody at a desk writing down what she said she would
 * do. Two are also raised by the system itself: `follow_up` from a call nobody answered and from a
 * dispute or a failed refund, `chase` from a bank payment that failed. `renewal` is raised by a
 * person and by nothing else, because no job writes one.
 */
export const TASK_KIND_LABELS: Record<TaskKind, string> = Object.freeze({
  renewal: 'A renewal',
  filing: 'A filing',
  chase: 'Chasing something up',
  admin: 'Office work',
  follow_up: 'A follow-up',
});

/**
 * What came of a call or an email (ops #86), as the office says it on a quote's follow-up. The words
 * are `data/outreach-log.md`'s, and `none` is the row that keeps a reply rate honest.
 */
export const TOUCH_OUTCOME_LABELS: Record<TouchOutcome, string> = Object.freeze({
  sent: 'Sent',
  replied: 'They replied',
  quoted: 'Quoted',
  booked: 'Booked',
  declined: 'Declined',
  gated: 'Stopped at a gatekeeper',
  none: 'No answer',
});

/** The ops-side names. The wire vocabulary is the website's, confirmed with its owner. */
export const ENQUIRY_STATE_LABELS: Record<EnquiryState, string> = Object.freeze({
  new: 'New',
  contacted: 'Contacted',
  quoted: 'Quoted',
  booked: 'Booked',
  lost: 'Lost',
});

/**
 * Why a row is being thrown away, in the words somebody would actually say. They are also exactly
 * what is written into the record, so the sentence in the audit trail and the words under the
 * finger that chose it are the same words.
 *
 * A PRIVACY REQUEST IS NOT SPAM AND IT IS NOT A TEST. Somebody asking to be forgotten is the one
 * reason on this list that comes from outside the business, and filing it as "something else"
 * loses the only fact about it that matters afterwards: what was removed, and why, is the answer
 * to a question that can be asked again months later by the person who asked for it.
 */
export const DISCARD_REASON_LABELS: Record<DiscardReason, string> = Object.freeze({
  test: 'A test',
  spam: 'Spam',
  duplicate: 'A duplicate',
  privacy_request: 'A privacy request',
  other: 'Something else',
});

/**
 * THE THREE WORDS THE PHONE SYSTEM READS OUT OF A TEXT (ops #25), as a pill beside it. Each is what
 * she typed, in capitals the way the carrier's own rules write it, so the office reads the pill as
 * the text rather than as something ops decided. A STOP and a START reach the register as facts of
 * their own (ops #29) and HELP is the phone system's to answer, so the pill says what came and
 * never what was done about it.
 */
export const INBOUND_KEYWORD_LABELS: Record<InboundKeyword, string> = Object.freeze({
  stop: 'Texted STOP',
  start: 'Texted START',
  help: 'Texted HELP',
});

/**
 * HOW A STOP IN HER WORDS, HELD FOR THE OFFICE, ENDED (ops #30, decided at ops #33's door), as the
 * thread's pill says it under her text. The office confirms or clears it; ops itself marks it after
 * five business days undecided, or finds it answered by a stop of her own.
 */
export const STOP_HOLD_OUTCOME_LABELS: Record<StopHoldOutcome, string> = Object.freeze({
  confirmed: 'Confirmed as a stop',
  cleared: 'Cleared, no stop',
  auto_suppressed: 'Became a stop, nobody deciding',
  superseded: 'Answered by their own stop',
});

/** Where a text stands. Only an open one is listed: handled and spam leave the list (ops #26). */

/**
 * WHERE A VISIT'S REMINDER BEFORE THE CLEAN STANDS (ops #78), as the label on its one line on the
 * visit's page. Nobody approves a reminder, so written and approved are both waiting to go, and why a
 * withdrawn one was withdrawn is the sentence beside it, from `REMINDER_WITHDRAWN_BECAUSE`.
 */
export const REMINDER_STATE_LABELS: Record<MessageIntentState, string> = Object.freeze({
  composed: 'Waiting to go',
  approved: 'Waiting to go',
  handed_off: 'With the phone system',
  delivered: 'Delivered',
  failed: 'Did not go',
  cancelled: 'Withdrawn',
  refused_by_consent: 'Not sent',
});

/**
 * WHY A VISIT'S REMINDER WAS WITHDRAWN (ops #78), finishing "The reminder ... was withdrawn". A change
 * to the visit, or a stop on her number with the visit where it was: never the one for the other.
 */
export const REMINDER_WITHDRAWN_BECAUSE: Record<ReminderWithdrawnFor, string> = Object.freeze({
  visit_changed: 'when the visit changed',
  carrier_stop: 'when their number sent STOP to the phone system',
  suppression: 'when the office marked their number do not contact',
  forget: 'when they asked to be forgotten',
  stop_in_words: 'when they texted a stop in their own words',
  stop_hold: 'when a stop in their own words was held for the office to confirm',
  email_stop: 'when a stop or a do-not-contact mark landed on them or their address',
});

/**
 * WHERE A CLIENT EMAIL STANDS (ops #76 O6, `docs/plan-o6-o9-emails.md` section 3.7), each a state ops or
 * home can leave one in, as the short label beside the sentence `emailStanding` says
 * (`screens/visit-detail/shared.ts`). A hold for its words and a hold by the switch are two labels,
 * since the office does a different thing about each.
 */
export type EmailStanding =
  | 'waiting_for_pdf'
  | 'waiting_for_door'
  | 'waiting'
  | 'with_home'
  | 'accepted'
  | 'delivered'
  | 'bounced_late'
  | 'did_not_go'
  | 'withdrawn'
  | 'refused'
  | 'held_by_switch'
  | 'held_words';

export const EMAIL_STANDING_LABELS: Record<EmailStanding, string> = Object.freeze({
  waiting_for_pdf: 'Waiting for its PDF',
  waiting_for_door: 'Waiting for their door',
  waiting: 'Waiting to go',
  with_home: 'With home',
  accepted: 'Accepted by Microsoft',
  delivered: 'Delivered',
  bounced_late: 'Bounced after delivery',
  did_not_go: 'Did not go',
  withdrawn: 'Withdrawn',
  refused: 'Refused by the register',
  held_by_switch: 'Held by the email switch',
  held_words: 'Held for its words',
});

/**
 * HOME'S WORD ON AN EMAIL THAT DID NOT GO (ops #76 O6, `docs/seams/home.md` 11.1), finishing "Did not
 * go:", in the words the office's call about it uses (`api/src/seams/email-state.ts`), and the drain's
 * own word for an email home never took. A word this list does not hold is said as home said it.
 */
export const HOME_EMAIL_SAID: Readonly<Record<string, string>> = Object.freeze({
  bounced: 'their address bounced it',
  refused: 'Microsoft refused it',
  unconfirmed: 'home could not confirm that it went, so it may have reached them',
  not_enabled: 'home is not emailing their address yet',
  expired: 'its last moment to go passed before home could send it',
  cancelled: 'it was recalled before it went',
  forgotten: 'they were forgotten at home before it went',
  daily_cap: 'ten emails had already gone to their address that day',
  kind_not_known: 'home does not know that kind of email',
  'home did not take it': 'home did not take it, after every try',
});

/**
 * WHY AN EMAIL WAS WITHDRAWN (ops #76 O6), by the words its push to home was parked with, finishing
 * "Withdrawn": a change to the visit its reminder was for, a stop or a mark on her or her address, her
 * forget, the office, the void of its invoice; and a change the office explained another way, by the
 * prefix its parked words carry. A word not here claims no reason. `superseded` is written by the void
 * alone (`voidInvoice`), which may leave no later email to that person at all, so its words name the
 * void and never a successor (concern 14's review, F1).
 */
export const EMAIL_WITHDRAWN_BECAUSE: Readonly<Record<string, string>> = Object.freeze({
  'withdrawn: visit changed': 'when the visit changed',
  'cancelled: stop': 'when a stop or a do-not-contact mark landed on them or their address',
  'cancelled: forget': 'when they asked to be forgotten',
  'cancelled: withdrawn': 'by the office',
  'cancelled: superseded': 'when the invoice was voided',
});

/** Withdrawn with a change the office explained, as `silenceChange` parks it: `withdrawn: <reason>`. */
export const EMAIL_WITHDRAWN_EXPLAINED = 'when the office recorded how they already know';

/**
 * WHAT KIND OF TEXT THE OFFICE IS WRITING (ops #32), as the composer offers them. Each is a kind the
 * carrier filed, and the office's own words go in every one of them.
 */
export const COMPOSER_KIND_LABELS: Record<ComposerKind, string> = Object.freeze({
  reply: 'Reply',
  booking_confirmation: 'Booking confirmation',
  visit_finished: 'Visit finished',
  access_problem: 'Could not get in',
});

/** Where an answer came from. */
export const FEEDBACK_SOURCE_LABELS: Record<FeedbackSource, string> = Object.freeze({
  link: 'From their link',
  home: 'From their own page',
  office: 'Recorded by the office',
  text: 'From a reply by text',
});

/** Where the question on one invoice stands, as the office reads it on the invoice screen. */
export const SURVEY_STATE_LABELS: Record<SurveyState, string> = Object.freeze({
  sent: 'Sent, not yet delivered',
  delivered: 'Delivered',
  reminded: 'Reminded',
  call_raised: 'The office asked to call',
  not_delivered: 'Never delivered',
});

/** Why the question on an invoice is held. */
export const SURVEY_HOLD_REASON_LABELS: Record<SurveyHoldReason, string> = Object.freeze({
  issue_reported: 'Their answer opened an issue',
  reply_received: 'They replied, and a person has to read it',
  office_hold: 'Held by the office',
});

/**
 * WHERE AN INVOICE'S AUTOMATIC CHARGE STANDS (ops #46, step 4), in the words the invoice screen shows.
 * `due` is carried by the vocabulary and written by nothing yet: the job charges in the run that opens it.
 */
export const INVOICE_CHARGE_STATE_LABELS: Record<InvoiceChargeState, string> = Object.freeze({
  waiting: 'Waiting for their answer',
  held: 'Held',
  due: 'Due on the next run',
  charging: 'Asked of Stripe',
  processing: 'Their bank payment is clearing',
  succeeded: 'Paid',
  declined: 'Declined',
  needs_authentication: 'Their bank wants them to confirm it',
  failed: 'Did not go through',
  cancelled: 'Not charged',
});

/** What opened the charge. Silence is never one. */
export const CHARGE_GATE_REASON_LABELS: Record<ChargeGateReason, string> = Object.freeze({
  answered_well: 'They said the clean went well',
  office_released: 'Released by the office',
});

/** Why the charge is held, each with what clears it. */
export const CHARGE_HOLD_REASON_LABELS: Record<ChargeHoldReason, string> = Object.freeze({
  issue_reported: 'An issue is open on the clean. It waits until the issue is closed.',
  reply_received: 'They replied, and a person has to read it.',
  survey_not_delivered:
    'The question never reached them. It waits for their answer, which the office can record.',
  office_hold: 'The question is held. It waits until a person releases it.',
  paying_by_zelle:
    'They said they are paying this one by Zelle. It waits until their Zelle is recorded, which ends it, or the office lifts the pause.',
});

/** Why the charge will not be made. */
export const CHARGE_CANCEL_REASON_LABELS: Record<ChargeCancelReason, string> = Object.freeze({
  paid_another_way: 'Paid another way, or nothing is owed',
  invoice_void: 'The invoice was voided',
  authorization_revoked: 'The saved method was stopped',
  office_cancelled: 'The office said not to charge this one',
});

/** What each kind of door lets her do at home, as the invitation asks the office to choose it. */
export const MEMBERSHIP_ROLE_LABELS: Record<MembershipRole, string> = Object.freeze({
  client: 'Client: pays, changes visits, edits the home and adds a person',
  member: 'Member: sees the household and asks, for the homes named',
});

/** Why a door ended, for the line under the pill. */

/** Where an ask stands, as the pill beside it says it. */

/** Why nobody could decide an ask any more, for the line under a cancelled one. */

/** Which way a mark holds, as the choice of a channel says it. */

/** Who wrote a mark down when nobody signed in did, as the line after the day says it. */
export const DO_NOT_CONTACT_DOOR_LABELS: Record<DoNotContactDoor, string> = Object.freeze({
  office: 'at the office',
  website: 'from the website',
  phone: 'from the phone system',
  home: 'through home',
  system: 'by ops itself',
});
